import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TrashIcon } from '@heroicons/react/24/outline';
import { webhookService, WebhookEndpointView, WebhookEndpointCreated } from '../../api/services';

/**
 * Gestion des webhooks sortants (TF-080) : URL de rappel + événements souscrits.
 * Le secret de signature HMAC-SHA256 n'est affiché qu'à la création.
 */
const Webhooks: React.FC = () => {
  const [endpoints, setEndpoints] = useState<WebhookEndpointView[]>([]);
  const [events, setEvents] = useState<string[]>([]);
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [justCreated, setJustCreated] = useState<WebhookEndpointCreated | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => webhookService.list().then(setEndpoints).catch((e) => toast.error(e?.response?.data?.message || 'Erreur'));

  useEffect(() => {
    load();
    webhookService.events().then(setEvents).catch(() => setEvents([]));
  }, []);

  const toggleEvent = (e: string) =>
    setSelected((s) => (s.includes(e) ? s.filter((x) => x !== e) : [...s, e]));

  const create = async () => {
    if (!url.trim()) { toast.error('URL requise'); return; }
    setBusy(true);
    try {
      const created = await webhookService.create(url.trim(), selected.length ? selected.join(',') : '*', description || undefined);
      setJustCreated(created);
      setUrl(''); setSelected([]); setDescription('');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de création');
    } finally { setBusy(false); }
  };

  const toggleActif = async (ep: WebhookEndpointView) => {
    try { await webhookService.update(ep.id, { actif: !ep.actif }); load(); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Erreur'); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Supprimer définitivement cet endpoint ?')) return;
    try { await webhookService.remove(id); load(); toast.success('Endpoint supprimé'); }
    catch (e: any) { toast.error(e?.response?.data?.message || 'Erreur'); }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Webhooks sortants</h2>
      <p className="text-sm text-gray-500">
        TuniFact envoie un POST signé (HMAC-SHA256, en-tête <code>X-TuniFact-Signature</code>)
        à votre URL à chaque événement souscrit. Vérifiez la signature avec le secret affiché à la création.
      </p>

      {justCreated && (
        <div className="p-4 bg-amber-50 border border-amber-300 rounded-lg text-sm space-y-2">
          <p className="font-semibold text-amber-900">Secret de signature — copiez-le maintenant, il ne sera plus affiché.</p>
          <code className="block bg-white border border-amber-200 rounded px-3 py-2 font-mono text-xs break-all">
            {justCreated.secret}
          </code>
          <button onClick={() => setJustCreated(null)} className="text-xs text-amber-700 underline">J'ai copié, masquer</button>
        </div>
      )}

      <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">URL de rappel</label>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://exemple.com/webhooks/tunifact"
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Événements (aucun coché = tous)</label>
          <div className="flex gap-2 flex-wrap">
            {events.map((e) => (
              <button key={e} type="button" onClick={() => toggleEvent(e)}
                      className={`text-xs px-2.5 py-1 rounded-full border ${
                        selected.includes(e) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'
                      }`}>
                {e}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Description (optionnel)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: intégration ERP partenaire"
                 className="w-full rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <button onClick={create} disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
          Ajouter l'endpoint
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3 text-left">URL</th>
              <th className="px-4 py-3 text-left">Événements</th>
              <th className="px-4 py-3 text-left">Créé le</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {endpoints.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500">Aucun endpoint</td></tr>}
            {endpoints.map((ep) => (
              <tr key={ep.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-xs break-all max-w-xs">{ep.url}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{ep.events}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(ep.createdAt).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActif(ep)}
                          className={`text-xs font-semibold rounded-full px-2 py-0.5 ${ep.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {ep.actif ? 'Actif' : 'Inactif'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => remove(ep.id)} className="text-red-600 hover:text-red-800">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Webhooks;
